/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { selectModelUsage } from '../../selectors';
import { LLM_USAGE_PERIODS } from '../../constants';

const DashboardMetrics = ({ period, onPeriodChange }) => {
  const usage = useSelector(selectModelUsage);

  const metricsData = [
    {
      title: 'Provider requests',
      value: (usage?.requestCount ?? 0).toLocaleString(),
      icon: <SendOutlinedIcon sx={{ color: '#2563eb' }} />,
      bgColor: '#eff6ff',
    },
    {
      title: 'Input tokens',
      value: (usage?.tokenUsage?.inputTokenCount ?? 0).toLocaleString(),
      icon: <SystemUpdateAltOutlinedIcon sx={{ color: '#2563eb' }} />,
      bgColor: '#eff6ff',
    },
    {
      title: 'Output tokens',
      value: (usage?.tokenUsage?.outputTokenCount ?? 0).toLocaleString(),
      icon: <FileUploadOutlinedIcon sx={{ color: '#2563eb' }} />,
      bgColor: '#eff6ff',
    },
    {
      title: 'Estimated cost',
      value: `$${Number(usage?.cost?.totalCost ?? 0).toFixed(2)}`,
      icon: <AttachMoneyOutlinedIcon sx={{ color: '#2563eb' }} />,
      bgColor: '#eff6ff',
    },
  ];

  return (
    <Box sx={{ p: 4, backgroundColor: '#f8fafc'}}>
      {/* Time Period Filter */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 1 }}
        >
          Time period
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
              },
            }}
          >
            {LLM_USAGE_PERIODS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Metric Cards Grid */}
      <Grid container spacing={2}>
        {metricsData.map((metric, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                height: '100%',
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  p: '16px !important',
                }}
              >
                {/* Icon Container */}
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '8px',
                    backgroundColor: metric.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {metric.icon}
                </Box>

                {/* Text Labels */}
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    {metric.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.125rem' }}
                  >
                    {metric.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardMetrics;
